import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import theme from 'theme';
import { HighchartsFlexWorkaroundContainer } from 'components/common/KeyFigureGraph/HighchartsFlexWorkaroundContainer';
import { formatBigMoney } from 'utils/formatting';

const findBiggestValue = (dataSets: GraphDataSet[]) => {
    const biggestValues = dataSets.map(dataSet =>
        Math.max(...dataSet.data.map(dataPoint => Math.abs(dataPoint)))
    );
    return Math.max(...biggestValues);
};

export interface GraphDataSet {
    seriesName: string;
    data: number[];
}

const formatData = (data: number[]) => {
    return data.map(dataPoint => {
        if (dataPoint === null) return null;
        return Math.round(dataPoint * 10) / 10;
    });
};

const formatMoneyData = (data: number[]) => {
    return data.map(dataPoint => {
        if (dataPoint === null) return null;
        return Math.round(dataPoint);
    });
};

const pickColor = (seriesIndex: number) => {
    switch (seriesIndex) {
        case 0:
            return 'rgb(124,181,236)';
        case 1:
            return 'darkgray';
        default:
            return 'black;';
    }
};

interface Props {
    categories: string[];
    dataSets: GraphDataSet[];
    height?: number;
    suffix: string;
    title?: string;
    yAxisLabel: string;
}

export const KeyFigureHighchart = (props: Props) => {
    const { categories, dataSets, height, suffix, title, yAxisLabel } = props;

    const biggestValue = findBiggestValue(dataSets);

    const series: Highcharts.SeriesColumnOptions[] = dataSets.map(
        (dataSet: GraphDataSet, index: number) => {
            return {
                type: 'column',
                name:
                    dataSets.length > 1
                        ? `${dataSet.seriesName} (${yAxisLabel})`
                        : yAxisLabel,
                data:
                    suffix === '€'
                        ? formatMoneyData(dataSet.data)
                        : formatData(dataSet.data),
                events: { legendItemClick: () => false },
                color: pickColor(index)
            };
        }
    );

    const options: Highcharts.Options = {
        chart: {
            zoomType: 'xy',
            height: `${height ? height : 265}px`,
            marginTop: 40,
            marginLeft: suffix === '€' ? 100 : 80
        },
        credits: {
            enabled: false
        },
        title: {
            text: title ? title : '',
            style: {
                fontSize: '16px',
                fontFamily: theme.fonts.medium
            }
        },

        tooltip: {
            shared: true,
            valueSuffix: ' ' + suffix,
            outside: true
        },
        xAxis: [
            {
                categories,
                crosshair: true
            }
        ],
        yAxis: [
            {
                labels: {
                    format: '{value}\xa0' + suffix,
                    formatter() {
                        switch (suffix) {
                            case '%':
                                return `${this.value}\xa0%`;
                            case '€':
                                if (biggestValue < 1000) {
                                    return formatBigMoney(this.value);
                                } else if (
                                    biggestValue >= 1000 &&
                                    biggestValue < 1000000
                                ) {
                                    return formatBigMoney(this.value, 'k');
                                }
                                return formatBigMoney(this.value, 'M');
                        }
                        return '' + this.value;
                    },
                    style: {
                        color: theme.colors.main
                    },
                    rotation: suffix === '€' ? -45 : 0
                },
                title: {
                    text: yAxisLabel,
                    style: {
                        color: theme.colors.main
                    }
                },
                plotLines: [
                    {
                        color: '#000000',
                        value: 0,
                        width: 1,
                        zIndex: 10
                    }
                ]
            }
        ],
        legend: {
            layout: 'vertical',
            align: 'center',
            verticalAlign: 'bottom',
            itemStyle: {
                fontWeight: 'normal'
            },
            itemMarginTop: 5
        },
        series
    };

    return (
        <HighchartsFlexWorkaroundContainer height={props.height}>
            <HighchartsReact highcharts={Highcharts} options={options} />
        </HighchartsFlexWorkaroundContainer>
    );
};
